import Ionicons from "@expo/vector-icons/Ionicons";
import {
  DateTimePickerAndroid,
  type DateTimePickerChangeEvent,
} from "@react-native-community/datetimepicker";
import {
  createElement,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { Platform, Pressable, StyleSheet, View } from "react-native";
import { useTranslation } from "react-i18next";

import { ThemedText } from "@/components/themed-text";
import { Radius, Spacing } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import { IOSPickerSheet } from "@/components/ui/ios-picker-sheet";
import i18n from "@/i18n";

/** Local date ("YYYY-MM-DD") → Date at local midnight, or null if malformed. */
function parseDate(value: string | null): Date | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const d = new Date(`${value}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function toLocalDate(date: Date): string {
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, "0");
  const d = `${date.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** "Jul 15" — includes the year only when it isn't the current one. */
export function formatDueDate(value: string): string {
  const d = parseDate(value);
  if (!d) return value;
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: d.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
  });
}

/** "27 September" — the fuller form used in goal rows, where there is room. */
export function formatGoalDueDate(value: string): string {
  const d = parseDate(value);
  if (!d) return value;
  return d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "long",
    year: d.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
  });
}

function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Compact due-date field for the goal composer: a chip that opens the native
 * date picker (or a web date input), showing the chosen date with a clear
 * button. `value` is a local "YYYY-MM-DD" string or null.
 */
export type GoalDuePickerHandle = { open: () => void };

export const GoalDuePicker = forwardRef<
  GoalDuePickerHandle,
  {
    value: string | null;
    onChange: (next: string | null) => void;
  }
>(function GoalDuePicker({ value, onChange }, ref) {
  useTranslation();
  const c = useTheme();
  const [showIOS, setShowIOS] = useState(false);

  const minimumDate = useMemo(() => startOfToday(), []);
  const selectedDate = parseDate(value) ?? minimumDate;

  function onNativeValueChange(
    _event: DateTimePickerChangeEvent,
    next: Date,
  ) {
    onChange(toLocalDate(next));
  }

  function openPicker() {
    if (Platform.OS === "android") {
      DateTimePickerAndroid.open({
        value: selectedDate,
        mode: "date",
        display: "default",
        minimumDate,
        onValueChange: onNativeValueChange,
        onDismiss: () => {},
      });
      return;
    }
    setShowIOS(true);
  }

  // Chip tap: Android opens its dialog, iOS opens the sheet. It used to TOGGLE
  // on iOS, because tapping the chip a second time was the only way to put the
  // inline wheel away. The sheet dismisses itself, so a second tap has nothing
  // left to close — and a toggle would now read as "the chip sometimes does
  // nothing".
  function toggle() {
    openPicker();
  }

  // Let the parent open the picker imperatively (e.g. tapping + with no date set).
  useImperativeHandle(ref, () => ({ open: openPicker }));

  const chip = (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={i18n.t(
        value ? "growth.dueDate.changeA11y" : "growth.dueDate.addA11y",
      )}
      onPress={Platform.OS === "web" ? undefined : toggle}
      style={[
        styles.chip,
        {
          borderColor: value ? c.tint : c.border,
          backgroundColor: value ? c.backgroundSelected : c.surfaceAlt,
        },
      ]}
    >
      <Ionicons
        name="calendar-outline"
        size={15}
        color={value ? c.tint : c.textMuted}
      />
      {Platform.OS === "web" ? (
        createElement("input", {
          type: "date",
          value: value ?? "",
          min: toLocalDate(minimumDate),
          "aria-label": i18n.t("growth.dueDate.inputA11y"),
          onChange: (e: { currentTarget: { value: string } }) =>
            onChange(e.currentTarget.value || null),
          style: StyleSheet.flatten([styles.webInput, { color: c.text }]),
        })
      ) : (
        <ThemedText variant="caption" tone={value ? "tint" : "secondary"}>
          {value
            ? i18n.t("growth.dueDate.due", { date: formatDueDate(value) })
            : i18n.t("growth.dueDate.add")}
        </ThemedText>
      )}
    </Pressable>
  );

  return (
    <View style={styles.root}>
      <View style={styles.row}>
        {chip}
        {value ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={i18n.t("growth.clearDueDateA11y")}
            onPress={() => onChange(null)}
            hitSlop={8}
            style={styles.clear}
          >
            <Ionicons name="close-circle" size={18} color={c.textMuted} />
          </Pressable>
        ) : null}
      </View>

      {/* The shared sheet, like every other wheel in the app. Inline it had no
          Done and no dismissal — see `ios-picker-sheet.tsx`. */}
      {Platform.OS === "ios" ? (
        <IOSPickerSheet
          visible={showIOS}
          value={selectedDate}
          onValueChange={onNativeValueChange}
          onClose={() => setShowIOS(false)}
          minimumDate={minimumDate}
          accessibilityLabel={i18n.t("growth.dueDate.inputA11y")}
        />
      ) : null}
    </View>
  );
});

const styles = StyleSheet.create({
  root: { alignSelf: "stretch", gap: Spacing.xs },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.xs,
  },
  chip: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: Spacing.md,
    paddingVertical: 6,
    borderRadius: Radius.md,
    borderWidth: 1,
  },
  clear: {
    padding: 2,
  },
  webInput: {
    borderWidth: 0,
    backgroundColor: "transparent",
    fontSize: 13,
  },
});
